import {
  Body1,
  Button,
  Caption1,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '@/config/msalConfig';
import { useTenant } from './TenantContext';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    minHeight: '100vh',
    padding: tokens.spacingHorizontalXXXL,
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalS,
  },
  diag: {
    marginTop: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
    maxWidth: '40rem',
    textAlign: 'left',
    overflowWrap: 'anywhere',
  },
});

/**
 * Shown to an AUTHENTICATED user whose resolved roles don't meet the gate's
 * requirement (SPA-Auth Standard rules 6 + 7). The most common real cause is
 * being signed into the WRONG account — so the PRIMARY action switches
 * accounts (`prompt: 'select_account'`), never a dead-end. The diagnostic
 * block makes any denial self-explanatory in a single screenshot.
 */
export function NoAccessPage({ productName = 'the AI Readiness Report' }: { productName?: string }) {
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const { diagnostics } = useTenant();
  const email = accounts[0]?.username ?? null;

  return (
    <main className={styles.root} data-testid="no-access" role="alert" aria-live="polite">
      <Title2>You don&apos;t have access to {productName}</Title2>
      <Body1>
        {email ? (
          <>
            You&apos;re signed in as <strong>{email}</strong>, which doesn&apos;t have access. If
            you have an account with access, sign in with that account.
          </>
        ) : (
          <>Your account doesn&apos;t have access.</>
        )}
      </Body1>
      <div className={styles.actions}>
        <Button
          appearance="primary"
          data-testid="switch-account"
          onClick={() =>
            void instance.loginRedirect({ ...loginRequest, prompt: 'select_account' })
          }
        >
          Sign in with a different account
        </Button>
        <Button
          appearance="secondary"
          data-testid="sign-out"
          onClick={() =>
            void instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })
          }
        >
          Sign out
        </Button>
      </div>
      <Caption1 className={styles.diag} data-testid="no-access-diagnostics">
        diagnostics — id-token roles: [{diagnostics.idTokenRoles.join(', ')}] · access-token
        roles: [{diagnostics.accessTokenRoles.join(', ')}]
        {diagnostics.acquireError ? ` · token error: ${diagnostics.acquireError}` : ''}
      </Caption1>
    </main>
  );
}
